import { registerActions } from "../actionRegistry";
import { getDomElement, fadeIn, fadeOut, setHtml, show, hide, setText } from "../utils/dom";
// This module builds result cards as HTML strings and hands them to innerHTML, so
// every interpolated string below must be escaped. `e` is short deliberately —
// it appears in a lot of template literals and a long name hurt readability
// enough that the escaping got skipped. The search term reaches this file with no
// user interaction (Front.tsx emits `data-init-autoload`), so this is the sink
// that mattered most.
import { escapeHtml as e } from "../utils/escapeHtml";
import { findprevnext, scrollto } from "../features/navigation";
import { modal_open_what, reordermode } from "../globalState";
import { easyselect, manualselect } from "../features/selection";
import { edittitle, savetitle } from "../features/gallery";
import { enterReorderMode, docollmove } from "../features/collections";
import { addDownloadCart2, removeFromDownloadCart2 } from "../features/downloadActions";
import { modal } from "./modal";
import { ptDlDo } from "../features/download";
import { api } from "../api";
import { toastError } from "./messages";

// State variables
let loadActive = 0;

/**
 * Quick-remove one artwork from the open list, from the trash button on its card.
 *
 * No confirmation dialog on purpose: this is a one-artwork, easily-repeated action on a
 * list the user owns, and the artwork itself is untouched — only its membership. The
 * bulk path (`removePhotos`) keeps its confirmation because it is admin-side and plural.
 */
async function removeArtworkFromList(
  listId: string,
  artworkId: number,
  cardEl: HTMLElement
): Promise<void> {
  // Guard against a second click landing while the first request is still in flight.
  if (cardEl.classList.contains("list-removing")) return;
  cardEl.classList.add("list-removing");
  try {
    await api.collections.removeFromList({ listId, artworkId });
  } catch {
    cardEl.classList.remove("list-removing");
    toastError("Could not remove the artwork from this list.");
    return;
  }
  // Drop the card rather than reloading the grid: a reload would lose the scroll position
  // in a long list, which is exactly where this button is worth having.
  cardEl.remove();
}

function findPrevNext(): void {
  findprevnext();
}

// ---------------------------------------------------------------------------
// Data types returned by /backend/load.php
// ---------------------------------------------------------------------------

interface RenderContext {
  isAdmin: boolean;
  showTitles: boolean;
  partnerId: number;
  partnerVip: boolean;
  roleDownload: number;
  optOnlyLowresDl: boolean;
  partner1457: boolean;
  imgRoot: string;
  extralayout: string;
  collUrl: string;
  /** The open list's id, empty unless this result set IS a list. */
  listId: string;
  /** Server's verdict on whether this user may edit that list — see `search/load`. */
  canRemoveFromList: boolean;
}

interface ImageItem {
  id: number;
  lowResPath: string;
  midResPath: string;
  md5: string;
  title: string;
  photographer: string;
  userId: number;
  status: number;
  statusWhen: number;
  qc: number;
  width: number;
  height: number;
  sortSales: number;
  sortDownloads: number;
  expProd: boolean;
  filterBadprint: boolean;
  downloaded: boolean;
  inList: boolean;
  inDlc: boolean;
}

interface CollectionResult {
  id: string;
  name: string;
  thumb: string;
  url: string;
}

interface ArtistResult {
  id: number;
  username: string;
  name: string;
  profilepic: string;
}

interface ArtistResults {
  collections: CollectionResult[];
  artists: ArtistResult[];
}

type LoadFilter = Record<string, string | number | undefined>;

function parseLoadFilter(filtersJson: string): LoadFilter {
  try {
    const raw: unknown = JSON.parse(filtersJson);
    const filter =
      Array.isArray(raw) && raw[0] ? (raw[0] as LoadFilter) : ((raw ?? {}) as LoadFilter);
    return filter;
  } catch {
    return {};
  }
}

function statusBubble(ok: boolean, labelOk: string, labelFail: string): string {
  const cls = ok ? "green" : "red";
  const ico = ok ? "check" : "times";
  const lbl = ok ? labelOk : labelFail;
  return `<span class="load-status-bubbles-${cls}"><i class="fas fa-${ico}-circle"></i> ${lbl}</span> `;
}

function buildArtistResultsHtml(artistResults: ArtistResults, ctx: RenderContext): string {
  let html = "";
  for (const c of artistResults.collections) {
    html += `<a href="/collection/${e(c.url)}"><div class="search-artist"><div class="search-artist-img">`;
    if (c.thumb) {
      html += `<img src="${ctx.imgRoot}${e(c.thumb)}">`;
    }
    html += `</div><div class="search-artist-name">${e(c.name)}</div><div class="search-artist-artworks">Collection</div></div></a>`;
  }
  for (const a of artistResults.artists) {
    html += `<a href="/artist/${e(a.username)}"><div class="search-artist"><div class="search-artist-img">`;
    if (a.profilepic) {
      html += `<img src="${ctx.imgRoot}${e(a.profilepic)}">`;
    } else {
      html += `<div style="width: 100px; height: 100px; border-radius: 100px; background: #eeeeee; margin: 0 auto;"></div>`;
    }
    html += `</div><div class="search-artist-name">${e(a.name)}</div><div class="search-artist-artworks">Artist</div></div></a>`;
  }
  return html;
}

function buildImageCardHtml(
  item: ImageItem,
  ctx: RenderContext,
  layout: string,
  selectionMode: boolean,
  postOptions: string,
  filter: LoadFilter
): string {
  const id = item.id;
  let html = "<div ";

  let modalTarget = `view:${id}`;
  if (filter.collection_id) {
    modalTarget += `:collection:${ctx.collUrl}`;
  }
  if (filter.q) {
    modalTarget += `:search:${filter.q}`;
  }
  const extralayout = ctx.extralayout ? ` ${ctx.extralayout}` : "";
  html += `data-modal-target="${e(modalTarget)}"`;
  // `load-image-titled` marks the tiles that carry the admin title+artist caption below the
  // image. The caption is fixed-size text inside a tile whose height is a `vw` fraction, so
  // the room left for it shrinks with the window while the text does not — on a narrow
  // screen it overflowed onto the row beneath. The class lets the stylesheet reserve that
  // room only where a caption exists, instead of padding every tile for everyone.
  const titled = ctx.showTitles ? " load-image-titled" : "";
  html += ` id="img-${id}" class="load-image load-image-${layout}${extralayout}${titled}">`;
  html += `<table width="100%" height="100%"><tr><td><center>`;

  // Image element
  let imgStyle = "display: none;";
  let shouldScrollTo = false;
  if (postOptions) {
    const parts = postOptions.split(":");
    if (parts[0] === "highlight" && !isNaN(Number(parts[1])) && Number(parts[1]) === id) {
      imgStyle += "border: 10px solid red;";
      shouldScrollTo = true;
    }
  }
  // Wrapped so the quick-remove button can be anchored to the IMAGE, not the tile. The
  // tile is a fixed box and the image is centred inside it, so a tile-anchored button
  // floats in the empty margin beside a portrait artwork. `display: inline-block` shrinks
  // the wrapper to the image and keeps the `<center>` centring intact.
  html += `<span class="load-image-frame">`;
  html += `<img style="${imgStyle}" id="imageobject-${id}" src="${ctx.imgRoot}${e(item.lowResPath)}"${shouldScrollTo ? ` data-scrollto="img-${id}"` : ""}>`;

  // Quick-remove from a list. Only drawn when the SERVER says this user may edit this list
  // — their own, or one shared with their reseller (see `canRemoveFromList`). Inside the
  // frame so it lands on the image's top-right corner, and hidden until the tile is
  // hovered so it does not compete with the artwork it sits on.
  if (ctx.canRemoveFromList) {
    html += `<span class="list-remove" id="list-remove-${id}" title="Remove from this list">`;
    html += `<i class="fas fa-trash"></i></span>`;
  }
  html += `</span>`;

  // Admin title editor
  if (ctx.showTitles) {
    html += `<div class="titleextras">`;
    html += `<div class="browse-titleartist" id="browse-titleartist-${id}">`;
    html += `<div class="browse-title-editor" id="artworktitle_editor_${id}" style="display: none;">`;
    html += `<input type="text" id="artworktitle_editor_input_${id}" value="${e(item.title)}">`;
    html += `</div>`;
    html += `<div class="browse-title" id="artworktitle_div_${id}">`;
    html += `<span id="artworktitle_${id}">${e(item.title)}</span> `;
    html += `<span class="edit-title-btn" style="font-size: 0.9em; color: #888; padding: 0px 0px 0px 3px; position: relative; bottom: 1px;"><i class="fas fa-edit"></i></span>`;
    html += `</div>`;
    // userId is a number, so it needs no escaping — the type is the guarantee.
    html += `<div class="browse-artist"><a href="/artist/${item.userId}">${e(item.photographer)}</a></div>`;
    html += `</div>`;
    html += `</div>`;
  }

  // Selection checkbox
  html += `<div style="display: ${selectionMode ? "block" : "none"};" class="select" id="select-${id}">`;
  html += `<i id="select-icon-${id}" class="far fa-square"></i></div>`;
  html += `<input class="select-store" type="hidden" id="selected-${id}" value="0">`;
  // Pre-warm target for the NEXT artwork in the view modal (`findprevnext` reads this and
  // puts it in #cache). It used to hold `https://1x.com/images/user/<md5>-hd2.jpg` — a
  // foreign host addressing renditions we do not serve, so the prefetch warmed nothing and
  // the modal still waited for the image. These are the two URLs the modal really uses.
  html += `<input type="hidden" id="hd-${id}" value="${e(ctx.imgRoot + (item.midResPath || item.lowResPath))}">`;
  html += `<input type="hidden" id="ld-${id}" value="${e(ctx.imgRoot + item.lowResPath)}">`;

  if (filter.inf_mode) {
    html += `<input type="hidden" id="infscroll-${id}" value="1">`;
  }

  if (filter["image-id"]) {
    html += `<div style="margin: 5px 0px 0px 0px;">${id}</div>`;
  }

  // Expanded products badge
  if (ctx.partner1457 && item.expProd) {
    const expStyle = item.downloaded ? ' style="margin-top: 30px;"' : "";
    html += `<span class="is-exp"${expStyle}>Expanded Products</span>`;
  }

  // Download buttons / status badges
  if (
    ctx.partnerId &&
    !filter.incoming &&
    !filter["info-sales"] &&
    !filter["info-downloads"] &&
    ctx.roleDownload === 1
  ) {
    if (item.downloaded && !ctx.showTitles && !(ctx.partnerVip && filter["show-copies-sold"])) {
      html += `<span class="already-downloaded">Downloaded</span>`;
    }
    if (item.inList && !ctx.showTitles) {
      html += `<span class="in-list">In list</span>`;
    }
    if (ctx.isAdmin) {
      html += `<span class="reorder-select" id="reorder-select-${id}" style="display: none;">`;
      html += `<input class="reorder-select-input" type="checkbox" id="reorder-select-input-${id}">`;
      html += `</span>`;
      html += `<div class="reorder-move-here reorder-move-here-disabled"><span class="reorder-move-here-label">MOVE HERE</span></div>`;
    }
    if (item.inDlc) {
      html += `<div id="view-download-buttons-${id}" class="view-download-buttons"><span style="font-size: 0.7em; font-weight: 400;"><i>Artwork added to download cart</i></span> &nbsp; <span class="button small dlbuttonfix remove-cart-btn" style="background:#c0392b;color:#fff;font-size:0.7em;" data-id="${id}"><i class="fas fa-times"></i> Remove</span></div>`;
    } else {
      html += `<div id="view-download-buttons-${id}" class="view-download-buttons"><span class="view-download-label">DOWNLOAD</span> `;
      if (!ctx.optOnlyLowresDl) {
        html += `<span class="button small yellow dlbuttonfix add-cart-btn"><i class="fas fa-plus"></i> Later</span> &nbsp; `;
      }
      html += `<span class="buttom small green dlbuttonfix quick-dl-btn" id="overlay-dlcart2${id}"><i class="fas fa-download"></i> Quick</span></div>`;
    }
  }

  // Sales / download stats
  if (ctx.partnerVip && filter["show-copies-sold"]) {
    html += `<div class="load-status">${item.sortSales} copies</div>`;
  }
  if (ctx.isAdmin) {
    if (filter["info-sales"]) {
      html += `<div class="load-status">${item.sortSales} copies</div>`;
    }
    if (filter["info-downloads"]) {
      html += `<div class="load-status">${item.sortDownloads} downloads</div>`;
    }
  }

  // Incoming admin status bubbles
  if (filter.incoming && ctx.isAdmin) {
    const mp = Math.round(((item.width * item.height) / 1000000) * 100) / 100;
    html += `<div class="load-status">`;
    html += `<div class="load-status-author">${e(item.photographer)}</div>`;
    html += `<div class="load-status-bubbles">`;
    html += statusBubble(item.status === 4 && item.statusWhen > 0, "For sale", "Not for sale");
    html += statusBubble(item.qc > 0, "QC", "QC");
    html += statusBubble(mp > 8, `${mp} MP`, `${mp} MP`);
    html += `</div></div>`;
  }

  html += `</center></td></tr></table></div>`;
  return html;
}

export function load(): void {
  if (loadActive === 1) return;

  const modeEl = getDomElement<HTMLInputElement>("lm_mode");
  const targetEl = getDomElement<HTMLInputElement>("lm_target");

  if (!modeEl || !targetEl) return;

  const targetContainerId = targetEl.value;
  const targetContainer = getDomElement(targetContainerId);

  if (!targetContainer) return;

  const activeEl = getDomElement<HTMLInputElement>("lm_active");
  if (activeEl?.value !== "1") return;

  loadActive = 1;

  // Show loader if exists
  const loaderEl = getDomElement("lm_loader");
  if (loaderEl) fadeIn(loaderEl);

  // Get blocked artworks list
  let blockArtworks = "";
  const artworksLoadedEl = getDomElement<HTMLInputElement>("lm_artworks_loaded");
  if (artworksLoadedEl?.value) {
    blockArtworks = artworksLoadedEl.value;
  }

  // Get load more options
  let loadOptions = "";
  const optionsEl = getDomElement<HTMLInputElement>("lm_options");
  if (optionsEl) {
    loadOptions = optionsEl.value;
  }

  // Get other required elements
  const filtersEl = getDomElement<HTMLInputElement>("lm_filters");
  const layoutEl = getDomElement<HTMLInputElement>("lm_layout");
  const fromEl = getDomElement<HTMLInputElement>("lm_from");
  const qtyEl = getDomElement<HTMLInputElement>("lm_qty");
  const selectEl = getDomElement<HTMLInputElement>("lm_select");

  if (!filtersEl || !layoutEl || !fromEl || !qtyEl || !selectEl) {
    loadActive = 0;
    return;
  }

  // Migrated to the raft tRPC route `search.load` (the PHP load.php endpoint was
  // removed). Calling it via the typed client uses the absolute SERVER_BASE_URL, so
  // it works regardless of which origin served the page.
  api.search
    .load({
      mode: modeEl.value,
      filters: filtersEl.value,
      layout: layoutEl.value as "bigthumbs" | "smallthumbs" | "tinythumbs",
      from: Number(fromEl.value) || 0,
      qty: Number(qtyEl.value) || 30,
      selection: selectEl.value === "1",
      options: loadOptions,
      blockArtworks,
    })
    .then((json) => {
      const items = json.items ?? [];
      const artistResults = json.artistResults ?? { collections: [], artists: [] };
      const results = json.results ?? 0;
      const artworksLoaded = json.artworksLoaded ?? "";
      const ctx = json.renderContext;

      // Update artworks loaded tracking
      if (artworksLoadedEl) {
        const infScrollActiveEl = getDomElement<HTMLInputElement>("lm_infscroll_active");
        if (infScrollActiveEl?.value !== "1") {
          if (artworksLoadedEl.value) {
            artworksLoadedEl.value += ",";
          }
          artworksLoadedEl.value += artworksLoaded;
        }
      }

      // Handle save search visibility
      const saveSearchEl = getDomElement("savesearch");
      if (saveSearchEl) {
        const pathname = window.location.pathname;
        if (
          pathname !== "/search" &&
          pathname !== "//search" &&
          pathname !== "/search/" &&
          !pathname.includes("following")
        ) {
          show(saveSearchEl, "inline");
        } else {
          hide(saveSearchEl);
        }
      }

      // Handle artists section on first load
      const artistsTarget = getDomElement("artists_target");
      if (parseInt(fromEl.value) === 0 && artistsTarget) {
        const hasArtists = artistResults.collections.length > 0 || artistResults.artists.length > 0;
        if (hasArtists && ctx) {
          setHtml(artistsTarget, buildArtistResultsHtml(artistResults, ctx));
          fadeIn(artistsTarget);
        } else {
          setText(artistsTarget, "");
          hide(artistsTarget);
        }
      }

      loadActive = 0;

      // Parse filter for card rendering
      const filter = parseLoadFilter(filtersEl.value);
      const layout = layoutEl.value;
      const selectionMode = selectEl.value === "1";

      // Append rendered image cards to target container
      const currentTarget = getDomElement(targetEl.value);
      if (currentTarget && ctx) {
        for (const item of items) {
          currentTarget.insertAdjacentHTML(
            "beforeend",
            buildImageCardHtml(item, ctx, layout, selectionMode, loadOptions, filter)
          );
        }

        // Bind event listeners for newly inserted image cards
        for (const item of items) {
          const cardEl = getDomElement(`img-${item.id}`);
          if (!cardEl) continue;
          if (ctx.isAdmin) {
            cardEl.addEventListener("mouseenter", (e) => {
              easyselect(String(item.id), e);
            });
          }

          // Modal click on card
          const modalTargetAttr = cardEl.getAttribute("data-modal-target");
          if (modalTargetAttr) {
            cardEl.addEventListener("click", () => {
              if (reordermode !== 0) return;
              const currentSelectMode = getDomElement<HTMLInputElement>("lm_select")?.value === "1";
              if (currentSelectMode) {
                manualselect(String(item.id));
              } else {
                modal(modalTargetAttr, "full", 0);
              }
            });
          }

          // Image onload: fadeIn + optional scrollTo
          const imgEl = getDomElement(`imageobject-${item.id}`);
          if (imgEl) {
            imgEl.addEventListener("load", () => {
              fadeIn(imgEl, 400, "");
              const scrollTarget = imgEl.getAttribute("data-scrollto");
              if (scrollTarget) scrollto(scrollTarget, 130);
            });
          }

          // Quick download button
          cardEl.querySelector(".quick-dl-btn")?.addEventListener("click", (e) => {
            e.stopPropagation();
            void ptDlDo(String(item.id));
          });

          if (ctx.showTitles) {
            cardEl.querySelector(".edit-title-btn")?.addEventListener("click", () => {
              edittitle(String(item.id));
            });
            getDomElement<HTMLInputElement>(
              `artworktitle_editor_input_${item.id}`
            )?.addEventListener("keyup", (e) => {
              if (e.key === "Enter") void savetitle(String(item.id));
            });
          }
          getDomElement(`list-remove-${item.id}`)?.addEventListener("click", (e) => {
            // stopPropagation for the same reason the checkbox below does it: this sits
            // INSIDE the card, whose own click handler opens the artwork. Without it,
            // removing an artwork also opened it.
            e.stopPropagation();
            void removeArtworkFromList(ctx.listId, item.id, cardEl);
          });
          getDomElement(`select-${item.id}`)?.addEventListener("click", (e) => {
            // stopPropagation, like every other in-card button here: the checkbox sits
            // INSIDE the card, and in select mode the card's own click handler also calls
            // manualselect(). Without this the click toggled twice and landed back where
            // it started, so the checkbox looked dead while clicking the artwork worked.
            e.stopPropagation();
            manualselect(String(item.id));
          });
          getDomElement<HTMLInputElement>(`reorder-select-input-${item.id}`)?.addEventListener(
            "change",
            () => {
              enterReorderMode();
            }
          );
          cardEl.querySelector(".reorder-move-here")?.addEventListener("click", () => {
            docollmove(String(item.id));
          });
          cardEl.querySelector(".add-cart-btn")?.addEventListener("click", (e) => {
            e.stopPropagation();
            void addDownloadCart2(String(item.id));
          });
          cardEl.querySelector(".remove-cart-btn")?.addEventListener("click", (e) => {
            e.stopPropagation();
            void removeFromDownloadCart2(String(item.id));
          });
        }
      }

      // Handle "no results" message
      const resultsCount = results;
      const hasArtistResults =
        artistResults.collections.length > 0 || artistResults.artists.length > 0;
      if (!hasArtistResults && resultsCount === 0 && parseInt(fromEl.value) === 0) {
        let showNotFound = true;
        const infScrollActiveEl = getDomElement<HTMLInputElement>("lm_infscroll_active");
        if (infScrollActiveEl?.value === "1") {
          showNotFound = false;
          const infBanner = getDomElement("inf-banner");
          if (infBanner) infBanner.remove();
        }

        if (showNotFound && currentTarget) {
          setHtml(
            currentTarget,
            '<div class="found-nothing-header">No artworks found</div><div class="found-nothing-sub">We didn\'t find any artworks, try another search term or remove filters</div>'
          );
        }
      }

      // Hide loader
      if (loaderEl) fadeOut(loaderEl);

      // Update from counter
      fromEl.value = String(parseInt(fromEl.value) + resultsCount);

      // Check if we've reached the end
      if (resultsCount !== parseInt(qtyEl.value)) {
        activeEl.value = "0";

        // Handle infinite scroll continuation
        const infScrollEnabledEl = getDomElement<HTMLInputElement>("lm_infscroll_enabled");
        const infScrollActiveEl = getDomElement<HTMLInputElement>("lm_infscroll_active");

        if (infScrollEnabledEl?.value === "1" && infScrollActiveEl?.value === "0") {
          infScrollActiveEl.value = "1";

          const infScrollFiltersEl = getDomElement<HTMLInputElement>("lm_infscroll_filters");
          const infScrollDiscreetEl = getDomElement<HTMLInputElement>("lm_infscroll_discreet");

          // Add infinite scroll banner
          let bannerHtml: string;
          if (infScrollDiscreetEl) {
            bannerHtml = `<div id="inf-banner" class="inf-banner"><div class="inf-banner-header2">${e(infScrollDiscreetEl.value)}</div></div><div class="inf-target" id="inf-target"></div>`;
          } else {
            bannerHtml =
              '<div id="inf-banner" class="inf-banner"><div class="inf-banner-header">End of results</div><div class="inf-banner-header2">but maybe these artworks can also interest you?</div></div><div class="inf-target" id="inf-target"></div>';
          }

          if (currentTarget) {
            currentTarget.insertAdjacentHTML("beforeend", bannerHtml);
          }

          // Update load more target and filters for infinite scroll
          targetEl.value = "inf-target";
          activeEl.value = "1";
          fromEl.value = "0";
          if (infScrollFiltersEl) {
            filtersEl.value = infScrollFiltersEl.value;
          }

          // Continue loading
          load();
        }
      }

      // Handle prev/next navigation for view modals
      const modalOpenWhat = modal_open_what;
      if (modalOpenWhat) {
        const viewIdEl = getDomElement("view-id");
        if (viewIdEl) {
          findPrevNext();
        }
      }
    })
    .catch((error: unknown) => {
      console.error("Failed to load content:", error);
      if (loaderEl) fadeOut(loaderEl);
      loadActive = 0;
    });
}

registerActions({
  load,
});
